import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Icons } from '@texas/components/shared/Icons';
import {
  SearchableMenuInput,
  SearchableMenuOption,
} from '@texas/components/shared/search/SearchableMenu';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';

interface StatisticsHeaderFiltersProps {
  sampleSize: number;
  onSampleSizeChange: (size: number) => void;
  selectedCustomerId?: number | null;
  selectedCustomerIds?: number[];
  onCustomerChange?: (customerId: number | null) => void;
  onCustomerIdsChange?: (customerIds: number[]) => void;
  selectedBranchIds: number[];
  onBranchChange: (branchIds: number[]) => void;
  isCustomerLocked?: boolean;
  isBranchLocked?: boolean;
  sampleSizeLabel?: string;
}

const sampleSizeOptions = [
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '200', value: 200 },
  { label: '500', value: 500 },
  { label: '1000', value: 1000 },
];

export function StatisticsHeaderFilters({
  sampleSize,
  onSampleSizeChange,
  selectedCustomerId,
  selectedCustomerIds,
  onCustomerChange,
  onCustomerIdsChange,
  selectedBranchIds,
  onBranchChange,
  isCustomerLocked,
  isBranchLocked,
  sampleSizeLabel,
}: StatisticsHeaderFiltersProps) {
  const { t } = useTranslation();

  const [customerSearchInput, setCustomerSearchInput] = useState('');

  const {
    branchesData: [branches, getBranches, loadingBranches],
    customersData: [customers, getCustomers, loadingCustomers],
  } = useMetadata();

  useEffect(() => {
    getBranches();
    getCustomers();
  }, [getBranches, getCustomers]);

  const filteredCustomers = useMemo(() => {
    if (customerSearchInput.length === 0) return [];
    return customers
      .filter((customer) =>
        customer.name.toLowerCase().includes(customerSearchInput.toLowerCase()),
      )
      .slice(0, Math.max(30, customers.length));
  }, [customers, customerSearchInput]);

  const isMultiSelectCustomer =
    selectedCustomerIds !== undefined && onCustomerIdsChange !== undefined;

  const customerDisplayText = () => {
    if (isMultiSelectCustomer) {
      if (selectedCustomerIds.length === 0) return t('filter.allCustomers');
      if (selectedCustomerIds.length === 1) {
        const customer = customers.find((c) => c.id === selectedCustomerIds[0]);
        return customer?.name ?? `1 ${t('general.customer').toLowerCase()}`;
      }
      return t('general.customersCount', { count: selectedCustomerIds.length });
    } else {
      if (selectedCustomerId) {
        const customer = customers.find((c) => c.id === selectedCustomerId);
        return customer?.name ?? t('filter.customer');
      }
      return t('filter.allCustomers');
    }
  };

  const branchDisplayText = () => {
    if (selectedBranchIds.length === 0) {
      return t('filter.allBranches');
    }
    if (selectedBranchIds.length === 1) {
      const branch = branches.find((b) => b.id === selectedBranchIds[0]);
      return branch?.name ?? t('general.branchesCount', { count: 1 });
    }
    return t('general.branchesCount', { count: selectedBranchIds.length });
  };

  return (
    <>
      <Menu isLazy={true}>
        <MenuButton
          as={Button}
          size="sm"
          minW="fit-content"
          variant="ghost"
          rightIcon={<Icons.ChevronDown boxSize={4} />}
        >
          <Text fontSize="sm">
            {sampleSizeLabel ?? t('widgets.common.sampleSize')}: {sampleSize}
          </Text>
        </MenuButton>
        <Portal>
          <MenuList>
            <MenuOptionGroup
              value={sampleSize.toString()}
              type="radio"
              onChange={(value) => {
                if (typeof value === 'string') {
                  onSampleSizeChange(parseInt(value, 10));
                }
              }}
            >
              {sampleSizeOptions.map((option) => (
                <MenuItemOption
                  key={option.value}
                  value={option.value.toString()}
                >
                  {option.label}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Portal>
      </Menu>

      {!isCustomerLocked && (
        <Menu isLazy={true} closeOnSelect={!isMultiSelectCustomer}>
          <MenuButton
            as={Button}
            size="sm"
            minW="fit-content"
            variant="ghost"
            rightIcon={<Icons.ChevronDown boxSize={4} />}
          >
            <Text fontSize="sm">{customerDisplayText()}</Text>
          </MenuButton>
          <Portal>
            <MenuList maxH="400px" overflowY="auto">
              <SearchableMenuInput
                placeholder={t('general.search')}
                value={customerSearchInput}
                onChange={setCustomerSearchInput}
              />
              {isMultiSelectCustomer ? (
                <>
                  <MenuItem
                    onClick={() => {
                      onCustomerIdsChange([]);
                      setCustomerSearchInput('');
                    }}
                    fontWeight={
                      selectedCustomerIds.length === 0 ? 'bold' : 'normal'
                    }
                  >
                    {t('filter.all')}
                  </MenuItem>
                  <MenuDivider />
                  {loadingCustomers ? (
                    <MenuItem isDisabled={true}>
                      {t('general.loading')}
                    </MenuItem>
                  ) : (
                    <>
                      <Flex flexDir="column" pb={2} gap={1} alignItems="start">
                        {filteredCustomers.map((customer) => (
                          <SearchableMenuOption
                            key={customer.id}
                            label={customer.name}
                            isSelected={selectedCustomerIds.includes(
                              customer.id,
                            )}
                            showSelectionIndicator={true}
                            onClick={() => {
                              const isSelected = selectedCustomerIds.includes(
                                customer.id,
                              );
                              if (isSelected) {
                                onCustomerIdsChange(
                                  selectedCustomerIds.filter(
                                    (id) => id !== customer.id,
                                  ),
                                );
                              } else {
                                onCustomerIdsChange([
                                  ...selectedCustomerIds,
                                  customer.id,
                                ]);
                              }
                            }}
                          />
                        ))}
                      </Flex>
                    </>
                  )}
                </>
              ) : (
                <>
                  <MenuItem
                    onClick={() => {
                      onCustomerChange?.(null);
                      setCustomerSearchInput('');
                    }}
                    fontWeight={selectedCustomerId === null ? 'bold' : 'normal'}
                  >
                    {t('filter.all')}
                  </MenuItem>
                  {loadingCustomers ? (
                    <MenuItem isDisabled={true}>
                      {t('general.loading')}
                    </MenuItem>
                  ) : (
                    <Flex flexDir="column" pb={2} gap={1} alignItems="start">
                      {filteredCustomers.map((customer) => (
                        <SearchableMenuOption
                          key={customer.id}
                          label={customer.name}
                          isSelected={customer.id === selectedCustomerId}
                          showSelectionIndicator={true}
                          onClick={() => onCustomerChange?.(customer.id)}
                        />
                      ))}
                    </Flex>
                  )}
                </>
              )}
            </MenuList>
          </Portal>
        </Menu>
      )}

      {!isBranchLocked && (
        <Menu isLazy={true} closeOnSelect={false}>
          <MenuButton
            as={Button}
            size="sm"
            minW="fit-content"
            variant="ghost"
            rightIcon={<Icons.ChevronDown boxSize={4} />}
          >
            <Text fontSize="sm">{branchDisplayText()}</Text>
          </MenuButton>
          <Portal>
            <MenuList maxH="400px" overflowY="auto">
              <MenuItem
                onClick={() => onBranchChange([])}
                fontWeight={selectedBranchIds.length === 0 ? 'bold' : 'normal'}
              >
                {t('filter.all')}
              </MenuItem>
              <MenuDivider />
              {loadingBranches ? (
                <MenuItem isDisabled={true}>{t('general.loading')}</MenuItem>
              ) : (
                <>
                  {branches.map((branch) => (
                    <MenuItemOption
                      key={branch.id}
                      value={branch.id.toString()}
                      isChecked={selectedBranchIds.includes(branch.id)}
                      onClick={() => {
                        const isSelected = selectedBranchIds.includes(
                          branch.id,
                        );
                        if (isSelected) {
                          onBranchChange(
                            selectedBranchIds.filter((id) => id !== branch.id),
                          );
                        } else {
                          onBranchChange([...selectedBranchIds, branch.id]);
                        }
                      }}
                    >
                      {branch.name}
                    </MenuItemOption>
                  ))}
                </>
              )}
            </MenuList>
          </Portal>
        </Menu>
      )}
    </>
  );
}
